import { setDoc, doc, getFirestore, Timestamp } from "firebase/firestore";
import {
  CampaignOverview,
  useAddArtists,
  useGenerateText,
  useSearchParams,
} from "Hooks";
import { Button } from "melodies-source/Button";
import { Spinner } from "melodies-source/Spinner";
import { SvgLeft } from "melodies-source/Svgs/Left";
import { nanoid } from "nanoid";
import { ShareLists } from "Providers/ListProvider/types";
import { useState } from "react";
import styled from "styled-components";
import { EditListForm } from "./EditListForm";
import { useNavigate } from "react-router-dom";
import { useUser } from "reactfire";
import { toast } from "react-toastify";
import { listDefaults } from "Providers/ListProvider/listDefaults";
import { countryBounds } from "utils";
import { Market } from "Providers/ListProvider/types";

export const CreateList = () => {
  const searchParams = useSearchParams();
  const artistId = searchParams.get("artistId");
  const ameUrl = searchParams.get("ameUrl");
  const [localList, setLocalList] = useState<Partial<ShareLists>>({
    ...listDefaults,
    version: "v2",
  });
  const [loading, setLoading] = useState<boolean>();
  const [draftLoading, setDraftLoading] = useState<boolean>();
  const { getCampaignOverview } = useGenerateText();
  const [listId] = useState(nanoid(6));
  const navigate = useNavigate();
  const { data: user } = useUser();
  const addArtists = useAddArtists();

  const createList = async (draft: boolean = false) => {
    try {
      const result = await getCampaignOverview({
        client: localList?.client,
        goal: localList?.campaignGoal,
        target: localList?.targetPersona,
        type: (
          localList?.type ?? "Sample"
        ).toLowerCase() as CampaignOverview["type"],
        artistCount: localList.artistCount,
      });

      // If the user didn't select any markets, default to the whole country
      // (USA) so tour dates auto-select when adding artists. This mirrors the
      // user picking the "Country" radio themselves: set marketType to
      // "Country" and add the USA market. The field default stays "DMA" while
      // creating; we only apply this fallback on save.
      const hasMarkets = Boolean(localList?.markets?.length);
      const markets: Market[] = hasMarkets
        ? localList.markets ?? []
        : [
            {
              id: "united-states",
              name: "United States",
              type: "Country",
              ...countryBounds.contiguousUS,
            },
          ];

      await setDoc(doc(getFirestore(), "ame_sharelists", listId), {
        ...localList,
        marketType: hasMarkets ? localList.marketType : "Country",
        markets,
        createdAt: Timestamp.now(),
        displayDate: Timestamp.now(),
        published: false,
        overview: result?.overview || "",
        createdBy: {
          uid: user.uid,
          name: user.displayName || "",
        },
      });

      if (artistId) {
        await addArtists([{ id: artistId }], listId);
      }

      toast.success("List has been created!");
    } catch (err) {
      console.log(err);
    } finally {
      if (ameUrl) {
        if (draft) {
          window.location.href = ameUrl;
        } else {
          navigate(`/admin/${listId}?${searchParams.toString()}`);
        }
      } else {
        navigate(draft ? "../" : `/admin/${listId}`);
      }
    }
  };

  return (
    <ListWrapper>
      <ActionContainer>
        <Button
          variant="basicText"
          w={170}
          onClick={() => {
            if (ameUrl) {
              window.location.href = ameUrl;
            } else {
              navigate("/admin");
            }
          }}
          leftIcon={<SvgLeft />}
          gap="8px"
        >
          Back to Search
        </Button>
        <SearchContainer>
          <Button
            variant="basicOutline"
            w={200}
            onClick={() => {
              createList(true);
              setDraftLoading(true);
            }}
            isDisabled={!localList?.campaign || draftLoading || loading}
          >
            {draftLoading ? <Spinner /> : "Save as Draft"}
          </Button>
          <Button
            variant="basic"
            w={230}
            onClick={() => {
              createList();
              setLoading(true);
            }}
            isDisabled={!localList?.campaign || draftLoading || loading}
          >
            {loading ? <Spinner /> : "Save and Add Artists"}
          </Button>
        </SearchContainer>
      </ActionContainer>
      <EditListForm
        setListDetails={setLocalList}
        list={localList}
        listId={listId}
      />
    </ListWrapper>
  );
};

export const ListWrapper = styled.div`
  width: clamp(900px, 80vw, 1170px);
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`;

export const ActionContainer = styled.div`
  width: clamp(900px, 80vw, 1170px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 50px auto 40px;
  ${(p) => p.theme.mediaQueries.mobile} {
    order: 1;
    margin: -20px auto;
    width: 100%;
    flex-direction: column;
    & > button {
      display: none;
    }
  }
`;

export const SearchContainer = styled.div`
  max-width: 400px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  ${(p) => p.theme.mediaQueries.mobile} {
    width: 100%;
    flex-direction: column-reverse;
    & > button {
      width: 100%;
    }
  }
`;
